// /vouchers/:id — Voucher Detail
//
// Scope extension beyond PAGES.md §3.33/§3.34. The SDLC does not define a
// detail page for vouchers, but every other admin list in the app has one
// and Voucher List needs a drill-down. Kept lightweight: read-only profile
// hero + aggregate KPIs + redemption log + Deactivate / Delete actions
// (Delete is OWNER-only).

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { categoryService } from '@/services/category.service';
import {
  PageContainer,
  ErrorBoundary,
  Button,
  Skeleton,
  EmptyState,
  ConfirmDialog,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { useStoreTimezone } from '@/lib/store-clock';
import {
  useVoucher,
  useVoucherStats,
  useVoucherRedemptions,
  useDeactivateVoucher,
  useDeleteVoucher,
  VoucherCodeChip,
  VoucherStatusPill,
  formatVoucherValue,
  daysUntil,
  formatVoucherDay,
  statusMeta,
  printVoucher,
} from '@/features/vouchers';
import type { VoucherRedemptionRow } from '@/features/vouchers';
import { ReportKpiRow, ReportTable, KpiIcons } from '@/features/reports';
import { formatDateTime } from '@/features/customers';

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CASHIER: 5,
};

type Row = VoucherRedemptionRow;

function makeRedemptionColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
  {
    key: 'redeemed_at',
    header: 'Redeemed',
    render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-300 tabular-nums">{formatDateTime(row.redeemed_at)}</span>,
  },
  {
    key: 'bill_number',
    header: 'Bill',
    render: (_v, row) => <span className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{row.bill_number}</span>,
  },
  {
    key: 'customer_name',
    header: 'Customer',
    render: (_v, row) => <span className="text-sm text-neutral-700 dark:text-neutral-300">{row.customer_name ?? 'Walk-in'}</span>,
  },
  {
    key: 'order_total',
    header: 'Order Total',
    numeric: true,
    render: (_v, row) => <span className="text-sm tabular-nums">{formatPaise(row.order_total, currency)}</span>,
  },
  {
    key: 'discount_amount',
    header: 'Discount',
    numeric: true,
    render: (_v, row) => <span className="text-sm font-bold text-success-700 dark:text-success-300 tabular-nums">−{formatPaise(row.discount_amount, currency)}</span>,
  },
  {
    key: 'location_name',
    header: 'Location',
    render: (_v, row) => <span className="text-xs text-neutral-500 dark:text-neutral-400">{row.location_name ?? '—'}</span>,
  },
  ];
}

function VoucherDetailContent({ id }: { id: string }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const timeZone = useStoreTimezone();
  const redemptionColumns = useMemo(() => makeRedemptionColumns(currency), [currency]);
  const voucherQ = useVoucher(id);
  const statsQ = useVoucherStats(id);
  const [page, setPage] = useState(1);
  const redemptionsQ = useVoucherRedemptions(id, page);
  const deactivateMutation = useDeactivateVoucher(id);
  const deleteMutation = useDeleteVoucher(id);
  // C03: a voucher for some categories names them ("Applies to: Snacks").
  const scopedCategoryIds = voucherQ.data?.applicable_category_ids ?? [];
  const categoriesQ = useQuery({
    queryKey: ['categories', 'voucher-scope'],
    queryFn: () => categoryService.list(),
    enabled: scopedCategoryIds.length > 0,
    staleTime: 60_000,
  });

  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const canDelete = roleLevel >= ROLE_LEVELS.OWNER;

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  async function handleDeactivate() {
    try {
      await deactivateMutation.mutateAsync();
      toast({ variant: 'success', title: 'Voucher deactivated' });
      setConfirmDeactivate(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Please try again.';
      toast({ variant: 'error', title: 'Could not deactivate', description: msg });
    }
  }

  async function handleDelete() {
    try {
      await deleteMutation.mutateAsync();
      toast({ variant: 'success', title: 'Voucher deleted' });
      navigate('/vouchers');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Please try again.';
      toast({ variant: 'error', title: 'Could not delete', description: msg });
    }
  }

  if (voucherQ.isLoading) {
    return (
      <>
        <Skeleton variant="rect" height={220} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={360} className="rounded-2xl" />
      </>
    );
  }

  const voucher = voucherQ.data;
  if (!voucher) {
    return (
      <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-10 animate-fade-in">
        <EmptyState
          variant="page"
          title="Voucher not found"
          description="This voucher may have been deleted or the link is invalid."
          action={{ label: 'Back to vouchers', onClick: () => navigate('/vouchers') }}
        />
      </div>
    );
  }

  const meta = statusMeta(voucher.status);
  const stats = statsQ.data;
  const days = daysUntil(voucher.expires_at);
  const isActive = voucher.status === 'ACTIVE';
  const categoryNames = new Map((categoriesQ.data?.data ?? []).map((c) => [c.id, c.name]));
  const scopeParts = [
    ...voucher.applicable_category_ids.map((cid) => categoryNames.get(cid) ?? 'a category'),
    ...(voucher.applicable_product_ids.length > 0
      ? [`${voucher.applicable_product_ids.length} product${voucher.applicable_product_ids.length === 1 ? '' : 's'}`]
      : []),
  ];
  const appliesTo = scopeParts.length > 0 ? scopeParts.join(', ') : 'Everything';

  // Print a customer-facing voucher card carrying a QR of the code, so a
  // cashier can scan it at the POS (BillDiscountModal → Scan) to apply it.
  async function handlePrint() {
    if (!voucher) return;
    const result = await printVoucher(voucher, currency, timeZone);
    if (!result.ok) {
      toast(
        result.reason === 'popup-blocked'
          ? { variant: 'error', title: 'Pop-up blocked', description: 'Allow pop-ups for this site to print the voucher.' }
          : { variant: 'error', title: 'Could not prepare voucher', description: 'Please try again.' },
      );
    }
  }

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${meta.gradient} p-6 sm:p-8 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(99,102,241,0.55)]`}>
        <div className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-black/15 blur-3xl" />

        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <VoucherStatusPill status={voucher.status} size="md" />
            <div className="mt-3">
              <VoucherCodeChip code={voucher.code} size="lg" copyable className="bg-white/95 !text-brand-700 !ring-white/30" />
            </div>
            <p className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight tabular-nums">{formatVoucherValue(voucher, currency)}</p>
            <p className="mt-1 text-sm text-white/85">
              {voucher.min_order_value > 0 ? `Min order ${formatPaise(voucher.min_order_value, currency)}` : 'No minimum order value'}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              {voucher.starts_at ? (
                <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold">Starts {formatVoucherDay(voucher.starts_at, timeZone)}</span>
              ) : null}
              {voucher.expires_at ? (
                <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold">Valid until {formatVoucherDay(voucher.expires_at, timeZone, true)}</span>
              ) : (
                <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold">No expiry</span>
              )}
              <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold truncate max-w-[18rem]">Applies to: {appliesTo}</span>
              {voucher.description ? (
                <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold truncate max-w-[14rem]">{voucher.description}</span>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <Button
              variant="secondary"
              onClick={handlePrint}
              className="!bg-white/20 hover:!bg-white/30 !text-white !border-white/30 backdrop-blur-sm"
            >
              <span className="inline-flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
                Print voucher
              </span>
            </Button>
            {isActive ? (
              <Button
                variant="secondary"
                onClick={() => setConfirmDeactivate(true)}
                className="!bg-white/20 hover:!bg-white/30 !text-white !border-white/30 backdrop-blur-sm"
              >
                Deactivate
              </Button>
            ) : null}
            {canDelete ? (
              <Button
                variant="danger"
                onClick={() => setConfirmDelete(true)}
                className="!bg-white !text-danger-600 hover:!bg-white/90"
              >
                Delete
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <ReportKpiRow
        tiles={[
          {
            id: 'used',
            title: 'Used / Max',
            value:
              voucher.max_uses != null
                ? `${voucher.used_count.toLocaleString('en-IN')} / ${voucher.max_uses.toLocaleString('en-IN')}`
                : `${voucher.used_count.toLocaleString('en-IN')} / ∞`,
            subLabel: stats?.utilisation_pct != null ? `${stats.utilisation_pct.toFixed(1)}% utilised` : undefined,
            tone: 'brand',
            icon: KpiIcons.receipt,
          },
          {
            id: 'discount',
            title: 'Total Discount Given',
            value: formatPaise(stats?.total_discount_given ?? 0, currency),
            tone: 'success',
            icon: KpiIcons.rupee,
          },
          {
            id: 'customers',
            title: 'Unique Customers',
            value: (stats?.unique_customers ?? 0).toLocaleString('en-IN'),
            tone: 'violet',
            icon: KpiIcons.users,
          },
          {
            id: 'days',
            title: 'Days Remaining',
            value: days == null ? '∞' : days < 0 ? 'Expired' : days.toString(),
            subLabel: voucher.expires_at ? formatVoucherDay(voucher.expires_at, timeZone, true) : 'No expiry',
            tone: days != null && days >= 0 && days <= 7 ? 'warning' : 'indigo',
            icon: KpiIcons.clock,
          },
        ]}
      />

      <ReportTable
        title="Redemption History"
        subtitle="Every bill where this voucher was applied."
        columns={redemptionColumns}
        data={(redemptionsQ.data?.data ?? []).map((r) => ({ ...r }))}
        loading={redemptionsQ.isFetching}
        totalCount={redemptionsQ.data?.meta.total}
        currentPage={page}
        onPageChange={setPage}
        emptyTitle="No redemptions yet"
        emptyDescription="Once a customer uses this voucher at checkout, the bill will show up here."
      />

      <ConfirmDialog
        open={confirmDeactivate}
        title="Deactivate voucher?"
        description={`This will prevent any further redemptions of ${voucher.code}. You can still view its history. This action is reversible by your backend team.`}
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        variant="warning"
        loading={deactivateMutation.isPending}
        onConfirm={handleDeactivate}
        onCancel={() => setConfirmDeactivate(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete voucher?"
        description={`Permanently delete ${voucher.code}. This cannot be undone. A voucher that has been redeemed cannot be deleted — deactivate it instead.`}
        confirmLabel="Delete permanently"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

function VoucherDetailPageInner({ id }: { id: string }) {
  const voucherQ = useVoucher(id);
  const code = voucherQ.data?.code ?? 'Detail';
  return (
    <PageContainer
      title={code === 'Detail' ? 'Voucher' : code}
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Vouchers', href: '/vouchers' }, { label: code }]}
    >
      <ErrorBoundary resetKey={`voucher-${id}`}>
        <VoucherDetailContent id={id} />
      </ErrorBoundary>
    </PageContainer>
  );
}

export function VoucherDetailPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return (
      <PageContainer title="Voucher" breadcrumbs={[{ label: 'Vouchers', href: '/vouchers' }]}>
        <div className="p-6 text-neutral-500">Missing voucher id.</div>
      </PageContainer>
    );
  }
  return <VoucherDetailPageInner id={id} />;
}
